import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Typography,
  Chip,
  Box,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from "@material-ui/core";
import {
  Search as SearchIcon,
  GetApp as DownloadIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Refresh as RefreshIcon,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  searchContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: "center"
  },
  statsContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap"
  },
  statCard: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    minWidth: 200
  },
  tableContainer: {
    marginTop: theme.spacing(2)
  },
  fileRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  folderRow: {
    backgroundColor: theme.palette.action.selected,
  },
  fileName: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  fileIcon: {
    color: theme.palette.text.secondary
  },
  folderIcon: {
    color: theme.palette.primary.main
  }
}));

const FileManager = () => {
  const classes = useStyles();

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [directory, setDirectory] = useState("public");
  const [stats, setStats] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [userId, setUserId] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadFiles();
    loadStats();
    loadUsers();
  }, [directory, pageNumber]);

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data.users || []);
    } catch (err) {
      console.error("Erro ao carregar usuários:", err);
    }
  };

  const loadFiles = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/file-manager", {
        params: { 
          directory, 
          searchParam, 
          pageNumber,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          userId: userId || undefined,
          contactNumber: contactNumber || undefined
        }
      });
      setFiles(data.files);
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get("/file-manager/stats");
      setStats(data);
    } catch (err) {
      console.error("Erro ao carregar estatísticas:", err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPageNumber(1);
    loadFiles();
  };

  const handleDownload = async (filePath, fileName) => {
    try {
      const response = await api.get("/file-manager/download", {
        params: { directory, filePath },
        responseType: 'blob'
      });

      // Cria um link temporário para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Download iniciado!");
    } catch (err) {
      toastError(err);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('pt-BR');
  };

  const getFileExtensionColor = (extension) => {
    const colors = {
      '.pdf': 'secondary',
      '.jpg': 'primary',
      '.jpeg': 'primary',
      '.png': 'primary',
      '.gif': 'primary',
      '.mp4': 'default',
      '.avi': 'default',
      '.mp3': 'default',
      '.ogg': 'default',
      '.doc': 'primary',
      '.docx': 'primary',
      '.xls': 'primary',
      '.xlsx': 'primary',
      '.txt': 'default',
      '.zip': 'secondary',
      '.rar': 'secondary',
    };
    return colors[extension] || 'default';
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Visualizar Arquivos</Title>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {/* Abas para escolher o diretório */}
        <Tabs
          value={directory}
          onChange={(e, newValue) => {
            setDirectory(newValue);
            setPageNumber(1);
          }}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Arquivos das Conversas" value="public" />
          <Tab label="Arquivos Outros" value="uploads" />
        </Tabs>

        {/* Estatísticas */}
        {stats && (
          <Box className={classes.statsContainer}>
            <Paper className={classes.statCard}>
              <Typography variant="subtitle2" color="textSecondary">
                Arquivos das Conversas
              </Typography>
              <Typography variant="h6">
                {stats.public.formatted}
              </Typography>
            </Paper>
            <Paper className={classes.statCard}>
              <Typography variant="subtitle2" color="textSecondary">
                Arquivos Outros
              </Typography>
              <Typography variant="h6">
                {stats.uploads.formatted}
              </Typography>
            </Paper>
            <Paper className={classes.statCard}>
              <Typography variant="subtitle2" color="textSecondary">
                Total
              </Typography>
              <Typography variant="h6">
                {stats.total.formatted}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Busca e Filtros */}
        <Box className={classes.searchContainer}>
          <form onSubmit={handleSearch} style={{ width: '100%' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar arquivos..."
                  value={searchParam}
                  onChange={(e) => setSearchParam(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Data Inicial"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Data Final"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel>Usuário</InputLabel>
                  <Select
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    label="Usuário"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Número do Contato"
                  placeholder="Ex: 5527999..."
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value)}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                  style={{ height: '100%' }}
                >
                  Buscar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
        
        <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <Tooltip title="Atualizar">
            <IconButton onClick={loadFiles} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Tabela de Arquivos */}
        <div className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Usuário</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell align="right">Tamanho</TableCell>
                <TableCell align="center">Tipo</TableCell>
                <TableCell>Modificado em</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRowSkeleton columns={7} />
              ) : (
                <>
                  {files.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="textSecondary">
                          Nenhum arquivo encontrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    files.map((file, index) => (
                      <TableRow
                        key={index}
                        className={`${classes.fileRow} ${file.isDirectory ? classes.folderRow : ''}`}
                      >
                        <TableCell>
                          <div className={classes.fileName}>
                            {file.isDirectory ? (
                              <FolderIcon className={classes.folderIcon} />
                            ) : (
                              <FileIcon className={classes.fileIcon} />
                            )}
                            {file.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {file.userName || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {file.contactNumber || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {file.isDirectory ? '-' : formatBytes(file.size)}
                        </TableCell>
                        <TableCell align="center">
                          {!file.isDirectory && file.extension && (
                            <Chip
                              label={file.extension}
                              size="small"
                              color={getFileExtensionColor(file.extension)}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(file.modifiedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {!file.isDirectory && (
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                onClick={() => handleDownload(file.path, file.name)}
                                color="primary"
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Paginação */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Button
            variant="outlined"
            disabled={pageNumber === 1}
            onClick={() => setPageNumber(pageNumber - 1)}
          >
            Anterior
          </Button>
          <Typography>Página {pageNumber}</Typography>
          <Button
            variant="outlined"
            disabled={!hasMore}
            onClick={() => setPageNumber(pageNumber + 1)}
          >
            Próxima
          </Button>
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default FileManager;
